import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Clock, 
  Users, 
  ChefHat, 
  Heart, 
  ArrowLeft,
  Printer,
  Share2,
  CheckCircle2,
  Plus,
  Minus
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getCategoryLabel } from '../lib/categories';

export const RecipeDetail = () => {
  const { id } = useParams();
  const { user, userProfile, toggleFavorite } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedIngredients, setCheckedIngredients] = useState([]);
  const [servings, setServings] = useState(4);
  const [originalServings, setOriginalServings] = useState(4);

  const isFavorite = userProfile?.favorites?.includes(id);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const docRef = doc(db, 'recipes', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setRecipe(data);
          setServings(data.servings || 4);
          setOriginalServings(data.servings || 4);
        }
      } catch (error) {
        console.error('Error fetching recipe:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [id]);

  const handleFavoriteClick = async () => {
    if (user) {
      await toggleFavorite(id);
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: recipe?.title,
          text: recipe?.description,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link in Zwischenablage kopiert!');
    }
  };

  // Funktion zum Anpassen der Zutatenmenge
  const adjustIngredient = (ingredient) => {
    if (originalServings === servings) return ingredient;
    
    const ratio = servings / originalServings;
    
    // Regex um Zahlen am Anfang zu finden (z.B. "200g", "2 EL", "1/2 TL")
    const match = ingredient.match(/^(\d+(?:[.,]\d+)?(?:\/\d+)?)\s*(.*)/);
    
    if (match) {
      let number = match[1];
      const rest = match[2];
      
      // Brüche behandeln (z.B. 1/2)
      if (number.includes('/')) {
        const [num, denom] = number.split('/');
        number = parseFloat(num) / parseFloat(denom);
      } else {
        number = parseFloat(number.replace(',', '.'));
      }
      
      const adjusted = number * ratio;
      
      // Runden auf sinnvolle Werte
      let displayNumber;
      if (adjusted < 1) {
        displayNumber = Math.round(adjusted * 10) / 10;
      } else if (adjusted < 10) {
        displayNumber = Math.round(adjusted * 2) / 2; // Auf 0.5 runden
      } else {
        displayNumber = Math.round(adjusted);
      }
      
      // Formatierung
      if (displayNumber === Math.floor(displayNumber)) {
        return `${displayNumber} ${rest}`;
      } else {
        return `${displayNumber.toString().replace('.', ',')} ${rest}`;
      }
    }
    
    return ingredient;
  };

  const increaseServings = () => setServings(s => Math.min(s + 1, 20));
  const decreaseServings = () => setServings(s => Math.max(s - 1, 1));

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 animate-pulse">
        <div className="max-w-4xl mx-auto">
          <div className="h-96 bg-white rounded-3xl mb-8" />
          <div className="h-8 bg-white rounded w-1/2 mb-4" />
          <div className="h-4 bg-white rounded w-full mb-2" />
          <div className="h-4 bg-white rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-navy mb-4">
            Rezept nicht gefunden
          </h2>
          <Link to="/rezepte">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zurück zu den Rezepten
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/rezepte" 
          className="inline-flex items-center gap-2 text-navy hover:text-terracotta mb-6 transition-colors"
          data-testid="back-to-recipes"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Zurück zu Rezepte</span>
        </Link>

        {/* Hero Image */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative aspect-video rounded-3xl overflow-hidden mb-8"
        >
          <img
            src={recipe.imageUrl || 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg'}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
          
          {/* Actions */}
          <div className="absolute top-4 right-4 flex gap-2">
            {user && (
              <button
                onClick={handleFavoriteClick}
                className={cn(
                  "p-3 rounded-full transition-all",
                  isFavorite 
                    ? "bg-terracotta text-white" 
                    : "bg-white/90 backdrop-blur-sm text-navy hover:bg-terracotta hover:text-white"
                )}
                data-testid="recipe-favorite-btn"
              >
                <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
              </button>
            )}
            <button
              onClick={handleShare}
              className="p-3 bg-white/90 backdrop-blur-sm text-navy hover:bg-sage hover:text-white rounded-full transition-all"
              data-testid="recipe-share-btn"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={handlePrint}
              className="p-3 bg-white/90 backdrop-blur-sm text-navy hover:bg-sage hover:text-white rounded-full transition-all print:hidden"
              data-testid="recipe-print-btn"
            >
              <Printer className="w-5 h-5" />
            </button>
          </div>

          {/* Category Badge */}
          {recipe.category && (
            <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-navy text-sm font-semibold px-4 py-2 rounded-full">
              {getCategoryLabel(recipe.category)}
            </span>
          )}
        </motion.div>

        {/* Title & Meta */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-4">
            {recipe.title}
          </h1>
          
          {recipe.description && (
            <p className="text-olive text-lg mb-6">{recipe.description}</p>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap gap-6">
            {recipe.prepTime && (
              <div className="flex items-center gap-2 text-navy">
                <Clock className="w-5 h-5 text-terracotta" />
                <span><strong>{recipe.prepTime}</strong> Minuten</span>
              </div>
            )}
            {recipe.servings && (
              <div className="flex items-center gap-2 text-navy">
                <Users className="w-5 h-5 text-terracotta" />
                <span><strong>{recipe.servings}</strong> Portionen</span>
              </div>
            )}
            {recipe.difficulty && (
              <div className="flex items-center gap-2 text-navy">
                <ChefHat className="w-5 h-5 text-terracotta" />
                <span className="capitalize">{recipe.difficulty}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ingredients */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-3xl p-6 shadow-sm sticky top-24">
              <h2 className="font-display text-xl font-semibold text-navy mb-4">
                Zutaten
              </h2>
              
              {/* Portionen-Rechner */}
              <div className="flex items-center justify-between bg-bone-alt rounded-xl p-3 mb-4">
                <span className="text-navy font-medium">Portionen</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={decreaseServings}
                    className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-navy hover:bg-terracotta hover:text-white transition-colors"
                    data-testid="decrease-servings"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-bold text-navy">{servings}</span>
                  <button
                    onClick={increaseServings}
                    className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-navy hover:bg-terracotta hover:text-white transition-colors"
                    data-testid="increase-servings"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {servings !== originalServings && (
                <p className="text-xs text-olive mb-3 text-center">
                  Original: {originalServings} Portionen
                </p>
              )}
              
              <ul className="space-y-3">
                {recipe.ingredients?.map((ingredient, index) => (
                  <li 
                    key={index}
                    className="flex items-start gap-3 cursor-pointer group"
                    onClick={() => toggleIngredient(index)}
                  >
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all",
                      checkedIngredients.includes(index)
                        ? "bg-sage border-sage"
                        : "border-olive/30 group-hover:border-sage"
                    )}>
                      {checkedIngredients.includes(index) && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className={cn(
                      "text-navy transition-all",
                      checkedIngredients.includes(index) && "line-through text-olive/50"
                    )}>
                      {adjustIngredient(ingredient)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-display text-xl font-semibold text-navy mb-6">
                Zubereitung
              </h2>
              <ol className="space-y-6">
                {recipe.instructions?.map((instruction, index) => (
                  <li key={index} className="flex gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-terracotta/10 rounded-full flex items-center justify-center text-terracotta font-bold">
                      {index + 1}
                    </span>
                    <p className="text-navy pt-1">{instruction}</p>
                  </li>
                ))}
              </ol>
            </div>

            {/* Tips */}
            {recipe.tips && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-accent/30 rounded-3xl p-6 sm:p-8 mt-6"
              >
                <h3 className="font-display text-lg font-semibold text-navy mb-3">
                  Tipps vom Koch
                </h3>
                <p className="text-navy">{recipe.tips}</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};
